import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TextInput,
  StyleSheet,
  ScrollView,
} from "react-native";
import { X } from "lucide-react";
import { getNoteTypeDisplayName } from "./encounterTypeLabels";

type Props = {
  visible: boolean;
  onClose: () => void;
  onSelect: (type: string, noteDate: string) => void;
  loading: boolean;
  error?: string | null;
  types: string[];
  allowCreate?: boolean;
  onCreateType?: (name: string) => Promise<void>;
  noteDate: string;
  maxDate?: string;
  onChangeNoteDate: (value: string) => void;
  disableNoteDate?: boolean;
  warningConfig?: {
    level: "small" | "tiny";
    message: string;
    sizeLabel: string;
    onAccept: () => void;
    onCancel: () => void;
  };
  warningAcknowledged?: boolean;
};

export default function EncounterTypeModal({
  visible,
  onClose,
  onSelect,
  loading,
  error,
  types,
  allowCreate = true,
  onCreateType,
  noteDate,
  maxDate,
  onChangeNoteDate,
  disableNoteDate = false,
  warningConfig,
  warningAcknowledged = true,
}: Props) {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!onCreateType) return;
    const name = newName.trim();
    if (!name) {
      setLocalErr("Please enter a name.");
      return;
    }
    setCreating(true);
    try {
      await onCreateType(name);
      setShowAdd(false);
      setNewName("");
      setLocalErr(null);
      onSelect(name, noteDate);
    } catch (e: any) {
      setLocalErr(e?.message || "Failed to create note type.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>Select Note Type</Text>
          <Text style={styles.subtitle}>What type of recording is this?</Text>

          {warningConfig && !warningAcknowledged ? (
            <View
              style={[
                styles.warningCard,
                warningConfig.level === "tiny"
                  ? styles.warningCardSevere
                  : styles.warningCardMild,
              ]}
            >
              <Text style={styles.warningTitle}>Small audio detected</Text>
              <Text style={styles.warningMessage}>{warningConfig.message}</Text>
              <Text style={styles.warningSize}>{warningConfig.sizeLabel}</Text>
              <View style={styles.warningButtonRow}>
                <TouchableOpacity
                  style={[
                    styles.btn,
                    styles.btnOutline,
                    styles.warningCancelBtn,
                  ]}
                  onPress={warningConfig.onCancel}
                >
                  <Text style={styles.btnText}>Cancel upload</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.btn,
                    styles.btnPrimary,
                    styles.warningContinueBtn,
                  ]}
                  onPress={warningConfig.onAccept}
                >
                  <Text style={styles.btnPrimaryText}>Continue</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <>
              <View style={styles.dateSection}>
                <Text style={styles.dateLabel}>Note date</Text>
                <View style={styles.dateRow}>
                  <input
                    type="date"
                    value={noteDate}
                    onChange={(event) => onChangeNoteDate(event.target.value)}
                    max={maxDate}
                    disabled={disableNoteDate}
                    style={{
                      border: "1px solid #C4C6CD",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 14,
                      color: "#282B33",
                      backgroundColor: disableNoteDate ? "#F5F6F8" : "#FFFFFF",
                      minWidth: 170,
                      cursor: disableNoteDate ? "not-allowed" : "pointer",
                    }}
                  />
                  {noteDate ? (
                    <TouchableOpacity
                      style={[
                        styles.dateClearBtn,
                        disableNoteDate && styles.dateClearBtnDisabled,
                      ]}
                      onPress={() => {
                        if (!disableNoteDate) {
                          onChangeNoteDate("");
                        }
                      }}
                      disabled={disableNoteDate}
                    >
                      <Text style={styles.dateClearText}>Clear</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>

              {loading ? (
                <View style={styles.rowCenter}>
                  <ActivityIndicator size="small" color="#0085FF" />
                  <Text style={styles.loadingText}>Loading note types...</Text>
                </View>
              ) : (
                <ScrollView
                  style={{ maxHeight: 320 }}
                  keyboardShouldPersistTaps="handled"
                >
                  {types.map((t) => (
                    <TouchableOpacity
                      key={t.toLowerCase()}
                      style={styles.typeBtn}
                      onPress={() => onSelect(t, noteDate)}
                    >
                      <Text style={styles.typeText}>
                        {getNoteTypeDisplayName(t)}
                      </Text>
                    </TouchableOpacity>
                  ))}

                  {(error || localErr) && (
                    <Text style={styles.err}>{error || localErr}</Text>
                  )}

                  {allowCreate &&
                    onCreateType &&
                    (showAdd ? (
                      <View style={styles.addWrap}>
                        <TextInput
                          value={newName}
                          onChangeText={setNewName}
                          placeholder="Enter new note type"
                          style={styles.input}
                          autoFocus
                        />
                        <View style={styles.addRow}>
                          <TouchableOpacity
                            style={[styles.btn, styles.btnPrimary]}
                            onPress={handleCreate}
                            disabled={creating}
                          >
                            {creating ? (
                              <ActivityIndicator size="small" color="#FFF" />
                            ) : (
                              <Text style={styles.btnPrimaryText}>
                                Save note type
                              </Text>
                            )}
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={styles.btn}
                            onPress={() => {
                              setShowAdd(false);
                              setNewName("");
                              setLocalErr(null);
                            }}
                          >
                            <Text style={styles.btnText}>Cancel</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.addTypeBtn}
                        onPress={() => {
                          setShowAdd(true);
                          setLocalErr(null);
                        }}
                      >
                        <Text style={styles.addTypeText}>
                          Add a new note type
                        </Text>
                      </TouchableOpacity>
                    ))}
                </ScrollView>
              )}
            </>
          )}

          <TouchableOpacity style={styles.close} onPress={onClose}>
            <X size={16} color="#555" />
            <Text style={styles.closeText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 400,
  },
  title: { fontSize: 20, fontWeight: "600", color: "#333", marginBottom: 8 },
  subtitle: { fontSize: 16, color: "#666", marginBottom: 20 },
  dateSection: { marginBottom: 16 },
  dateLabel: { fontSize: 13, color: "#555", marginBottom: 6 },
  dateRow: { flexDirection: "row", alignItems: "center" },
  dateClearBtn: { marginLeft: 8, paddingVertical: 6, paddingHorizontal: 8 },
  dateClearBtnDisabled: { opacity: 0.4 },
  dateClearText: { color: "#0085FF", fontSize: 12, fontWeight: "500" },
  rowCenter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 16,
  },
  loadingText: { marginLeft: 8, color: "#555", fontSize: 14 },
  typeBtn: {
    backgroundColor: "#F5F5F5",
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#EEE",
  },
  typeText: { fontSize: 16, color: "#333", textAlign: "center" },
  err: {
    color: "#E53935",
    fontSize: 14,
    textAlign: "center",
    marginTop: 4,
    marginBottom: 8,
  },
  addTypeBtn: {
    backgroundColor: "#FFF",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 8,
    borderWidth: 1,
    borderColor: "#000",
  },
  addTypeText: { color: "#000", fontSize: 15, fontWeight: "500" },
  addWrap: {
    backgroundColor: "#F9F9F9",
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    marginTop: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: "#CCC",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    backgroundColor: "#FFF",
  },
  addRow: { flexDirection: "row", marginTop: 16 },
  btn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#CCC",
    marginLeft: 8,
  },
  btnOutline: { backgroundColor: "#FFF", borderColor: "#CCC" },
  btnPrimary: {
    backgroundColor: "#0085FF",
    borderWidth: 0,
    marginRight: 8,
    marginLeft: 0,
  },
  btnPrimaryText: { color: "#FFF", fontSize: 14, fontWeight: "600" },
  btnText: { color: "#555", fontSize: 14, fontWeight: "500" },
  warningCard: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 18,
    marginBottom: 12,
  },
  warningCardMild: { backgroundColor: "#FFF8E1", borderColor: "#FFE0B2" },
  warningCardSevere: { backgroundColor: "#FFEBEE", borderColor: "#FFCDD2" },
  warningTitle: { fontSize: 16, fontWeight: "600", color: "#5D4037" },
  warningMessage: { fontSize: 14, color: "#5D4037", marginTop: 8 },
  warningSize: {
    fontSize: 13,
    color: "#795548",
    marginTop: 6,
    fontWeight: "600",
  },
  warningButtonRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 16,
  },
  warningCancelBtn: { marginLeft: 0, marginRight: 8 },
  warningContinueBtn: { marginLeft: 0 },
  close: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  closeText: { fontSize: 14, color: "#555", marginLeft: 6 },
});
